import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// ─── Response DTOs ───────────────────────────────────────────────────────────

export interface TimeEventDto {
    timeEventId: number;
    jobId: number | null;
    userId: string;
    startTime: string;
    endTime: string | null;
    description: string;
    isBillable: boolean;
    jobNumber: string | number | null;
    jobTitle: string | null;
    clientName: string | null;
    userName: string;
    teamName?: string | null;
    durationMs?: number;
    jobArchived?: boolean;
}

export interface RunningTimerResponse {
    success: boolean;
    runningEvent: TimeEventDto | null;
    /** Milliseconds since the latest presence ping for this user from any
     *  client, or null if no ping has been seen yet (e.g. server just
     *  restarted, or no client has ever pinged). Used by crash recovery
     *  to suppress the prompt when another client kept tracking while
     *  this one was down. */
    lastSeenMsAgo?: number | null;
    /** ISO-formatted UTC timestamp of the most recent long-running-timer
     *  alert dismissal for this user, or null if none. The client
     *  compares this against the running timer's start to decide whether
     *  a "Keep running" dismissal from another window applies here too. */
    longTimerDismissedAt?: string | null;
    /** Server's current UTC time (ISO-8601) when the response was built. Lets
     *  the client measure its clock offset from the server so the live timer's
     *  elapsed is computed in server-clock terms (immune to clock skew). */
    serverTime?: string | null;
}

export interface HeartbeatResponse {
    success: boolean;
}

export interface StartTimerResponse {
    success: boolean;
    runningEvent: TimeEventDto;
    stoppedEvent: TimeEventDto | null;
}

export interface StopTimerResponse {
    success: boolean;
    stoppedEvent: TimeEventDto;
}

export interface TimeEventsResponse {
    success: boolean;
    events: TimeEventDto[];
}

export interface SingleEventResponse {
    success: boolean;
    event: TimeEventDto;
}

export interface DeleteResponse {
    success: boolean;
}

export interface BulkUpdateResponse {
    success: boolean;
    updatedCount: number;
    events: TimeEventDto[];
}

export interface BulkDeleteResponse {
    success: boolean;
    deletedCount: number;
}

export interface RecentDescriptionsResponse {
    success: boolean;
    descriptions: string[];
}

export interface InputReportEntry {
    timeEventId: number;
    jobId: number;
    jobNumber: string;
    jobTitle: string;
    description: string;
    startTime: string;
    endTime: string;
    durationMs: number;
    isBillable: boolean;
}

export interface InputReportUser {
    userId: string;
    shortName: string;
    fullName: string;
    reason?: string;
    totalHours: number;
    entries: InputReportEntry[];
}

export interface InputReportResponse {
    success: boolean;
    date: string;
    availableUsers: InputReportUser[];
    awayUsers: InputReportUser[];
}

export interface PreviousDayTotalTimeWarning {
    userId: string;
    shortName: string;
    fullName: string;
    reportDate: string;
    previousWorkingDay: string;
    absent: boolean;
    /** Serialized from a .NET TimeSpan, e.g. "09:30:00" (hh:mm:ss, with a
     *  leading "d." day prefix if ever 24h or more - not expected here). */
    timeLogged: string;
    fullDay: boolean;
    timeThreshold: string;
}

export interface PreviousDayTotalTimeWarningsResponse {
    success: boolean;
    date: string;
    warnings: PreviousDayTotalTimeWarning[];
}

// ─── Request DTOs ────────────────────────────────────────────────────────────

export interface StartTimerRequest {
    jobId?: number | null;
    description?: string;
    isBillable?: boolean;
}

export interface UpdateRunningTimerRequest {
    jobId?: number | null;
    description?: string;
    isBillable?: boolean;
}

export interface CreateTimeEventRequest {
    jobId?: number | null;
    startTime: string;
    endTime: string;
    description?: string;
}

export interface UpdateTimeEventRequest {
    // Backend CreateTimeEventRequest requires both; always include them
    // (read current values off the FullCalendar event when only other fields changed).
    startTime: string;
    endTime: string;
    jobId?: number | null;
    description?: string;
    isBillable?: boolean;
}

export interface BulkUpdateRequest {
    ids: number[];
    changes: {
        jobId?: number | null;
        description?: string;
    };
}

export interface ReportQuery {
    dateFrom: string;
    dateTo: string;
    userIds?: string;
    clientIds?: string;
    jobIds?: string;
    billable?: string;
    rounding?: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
    providedIn: 'root',
})
export class TimeEventService {
    private baseUrl: string;

    constructor(
        private http: HttpClient,
        @Inject('BASE_URL') originUrl: string,
    ) {
        this.baseUrl = `${originUrl}api/time-events`;
    }

    // ─── Timer control ───────────────────────────────────────────────────────

    getRunningTimer(): Observable<RunningTimerResponse> {
        return this.http.get<RunningTimerResponse>(`${this.baseUrl}/getrunningtimer`);
    }

    /** Lightweight presence ping. Crash-recovery calls this every heartbeat
     *  tick so other clients (browser tab vs. desktop app) can tell this
     *  user still has an active client even when one of them goes down. */
    heartbeat(): Observable<HeartbeatResponse> {
        return this.http.post<HeartbeatResponse>(`${this.baseUrl}/heartbeat`, {});
    }

    /** Record a long-running-timer alert dismissal so sibling windows in
     *  other processes (browser tab ↔ desktop app — BroadcastChannel
     *  doesn't reach them) can suppress their copy of the alert on the
     *  next `getRunningTimer` poll. */
    dismissLongTimer(): Observable<HeartbeatResponse> {
        return this.http.post<HeartbeatResponse>(`${this.baseUrl}/dismisslongtimer`, {});
    }

    startTimer(req: StartTimerRequest): Observable<StartTimerResponse> {
        return this.http.post<StartTimerResponse>(`${this.baseUrl}/starttimer`, req);
    }

    stopTimer(): Observable<StopTimerResponse> {
        return this.http.post<StopTimerResponse>(`${this.baseUrl}/stoptimer`, {});
    }

    updateRunningTimer(req: UpdateRunningTimerRequest): Observable<RunningTimerResponse> {
        return this.http.post<RunningTimerResponse>(`${this.baseUrl}/updaterunningtimer`, req);
    }

    updateRunningTimerStartTime(startTime: string): Observable<RunningTimerResponse> {
        return this.http.post<RunningTimerResponse>(
            `${this.baseUrl}/updaterunningtimerstarttime`,
            { startTime },
        );
    }

    // ─── Entry CRUD ──────────────────────────────────────────────────────────

    getTimeEvents(dateFrom: string, dateTo: string, userId?: string): Observable<TimeEventsResponse> {
        let params = new HttpParams()
            .set('dateFrom', dateFrom)
            .set('dateTo', dateTo);
        if (userId) { params = params.set('userId', userId); }
        return this.http.get<TimeEventsResponse>(`${this.baseUrl}/gettimeevents`, { params });
    }

    createTimeEvent(req: CreateTimeEventRequest): Observable<SingleEventResponse> {
        return this.http.post<SingleEventResponse>(`${this.baseUrl}/createtimeevent`, req);
    }

    updateTimeEvent(id: number, req: UpdateTimeEventRequest): Observable<SingleEventResponse> {
        return this.http.post<SingleEventResponse>(`${this.baseUrl}/updatetimeevent/${id}`, req);
    }

    deleteTimeEvent(id: number): Observable<DeleteResponse> {
        return this.http.post<DeleteResponse>(`${this.baseUrl}/deletetimeevent/${id}`, {});
    }

    // ─── Bulk operations ─────────────────────────────────────────────────────

    updateBulkEvents(req: BulkUpdateRequest): Observable<BulkUpdateResponse> {
        return this.http.post<BulkUpdateResponse>(`${this.baseUrl}/updatebulkevents`, req);
    }

    deleteBulkEvents(ids: number[]): Observable<BulkDeleteResponse> {
        return this.http.post<BulkDeleteResponse>(`${this.baseUrl}/deletebulkevents`, { ids });
    }

    // ─── Header / convenience ────────────────────────────────────────────────

    getRecentEvents(limit: number = 20): Observable<TimeEventsResponse> {
        const params = new HttpParams().set('limit', limit.toString());
        return this.http.get<TimeEventsResponse>(`${this.baseUrl}/recent`, { params });
    }

    getRecentDescriptions(jobId: number, limit: number = 10): Observable<RecentDescriptionsResponse> {
        const params = new HttpParams()
            .set('jobId', jobId.toString())
            .set('limit', limit.toString());
        return this.http.get<RecentDescriptionsResponse>(
            `${this.baseUrl}/recent-descriptions`,
            { params },
        );
    }

    // ─── Reports ─────────────────────────────────────────────────────────────

    getReport(query: ReportQuery): Observable<TimeEventsResponse> {
        let params = new HttpParams()
            .set('dateFrom', query.dateFrom)
            .set('dateTo', query.dateTo);
        if (query.userIds) {
            for (const id of query.userIds.split(',')) { params = params.append('userIds', id); }
        }
        if (query.clientIds) {
            for (const id of query.clientIds.split(',')) { params = params.append('clientIds', id); }
        }
        if (query.jobIds) {
            for (const id of query.jobIds.split(',')) { params = params.append('jobIds', id); }
        }
        if (query.billable) { params = params.set('billable', query.billable); }
        return this.http.get<TimeEventsResponse>(`${this.baseUrl}/report`, { params });
    }

    getReportDownloadUrl(query: ReportQuery): string {
        const params = new URLSearchParams();
        params.set('dateFrom', query.dateFrom);
        params.set('dateTo', query.dateTo);
        if (query.userIds) {
            for (const id of query.userIds.split(',')) { params.append('userIds', id); }
        }
        if (query.clientIds) {
            for (const id of query.clientIds.split(',')) { params.append('clientIds', id); }
        }
        if (query.jobIds) {
            for (const id of query.jobIds.split(',')) { params.append('jobIds', id); }
        }
        if (query.billable) { params.set('billable', query.billable); }
        if (query.rounding && query.rounding !== 'none') { params.set('rounding', query.rounding); }
        return `${this.baseUrl}/download/report?${params.toString()}`;
    }

    getInputReport(date: string): Observable<InputReportResponse> {
        const params = new HttpParams().set('date', date);
        return this.http.get<InputReportResponse>(`${this.baseUrl}/inputreport`, { params });
    }

    getPreviousDayTotalTimeWarnings(date: string): Observable<PreviousDayTotalTimeWarningsResponse> {
        const params = new HttpParams().set('date', date);
        return this.http.get<PreviousDayTotalTimeWarningsResponse>(`${this.baseUrl}/previousdaytotaltimewarnings`, { params });
    }

    // ─── Helpers ─────────────────────────────────────────────────────────────

    /** Parse a TimeEventDto's startTime/endTime string to a Date object. */
    static parseDate(iso: string): Date {
        return new Date(iso);
    }

    /** Format a Date to an ISO string suitable for API requests. */
    static toIso(date: Date): string {
        return date.toISOString();
    }

    /** Format a date as YYYY-MM-DD (local time) for date range queries. */
    static toDateOnly(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
}
